import { motion } from "framer-motion";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { useViewMode } from "@/contexts/ViewModeContext";

const CALENDLY_LINK = "https://calendly.com/pragma-cx/discovery-call";

const packages = [
  {
    name: "Growth",
    tagline: "Perfect for growing businesses scaling their support",
    price: "Starting from $8",
    priceNote: "per agent/hour",
    minimumNote: "Flexible month-to-month",
    features: [
      { name: "Email support", included: true },
      { name: "Live chat support", included: true },
      { name: "WhatsApp integration", included: true },
      { name: "Real-time activity monitoring", included: true },
      { name: "Weekly performance reports", included: true },
      { name: "Dedicated onboarding", included: true },
      { name: "Training included", included: true },
      { name: "HR & payroll management", included: true },
      { name: "Flexible scaling (week-to-week)", included: true },
      { name: "No long-term contracts", included: true },
      { name: "Dedicated team lead", included: true },
      { name: "Phone support", included: false },
      { name: "Video call support", included: false },
      { name: "Dedicated account manager", included: false },
    ],
    cta: "Get Started",
    popular: false,
    color: "cyan",
  },
  {
    name: "Enterprise",
    tagline: "For established companies with mission-critical support needs",
    price: "Starting from $12",
    priceNote: "per agent/hour",
    minimumNote: "3-month commitment",
    features: [
      { name: "Everything in Growth, plus:", included: true, highlight: true },
      { name: "Phone support (inbound/outbound)", included: true },
      { name: "Video call support", included: true },
      { name: "Dedicated account manager", included: true },
      { name: "Dedicated team lead", included: true },
      { name: "Custom SLA agreements", included: true },
      { name: "Advanced analytics dashboard", included: true },
      { name: "Priority onboarding (3-5 days)", included: true },
      { name: "Custom training programs", included: true },
      { name: "API integrations available", included: true },
      { name: "99.9% uptime SLA", included: true },
      { name: "Monthly business reviews", included: true },
      { name: "24/7 priority support", included: true },
    ],
    cta: "Schedule Demo",
    popular: true,
    color: "magenta",
  },
];

const comparisonFeatures = [
  { name: "Email Support", growth: true, enterprise: true },
  { name: "Live Chat Support", growth: true, enterprise: true },
  { name: "WhatsApp Integration", growth: true, enterprise: true },
  { name: "Real-time Monitoring", growth: true, enterprise: true },
  { name: "Weekly Reports", growth: true, enterprise: true },
  { name: "Dedicated Team Lead", growth: true, enterprise: true },
  { name: "Cost per Hour", growth: "Starting from $8", enterprise: "Starting from $12" },
  { name: "Commitment", growth: "Month-to-month", enterprise: "3-month minimum" },
  { name: "Phone Support", growth: false, enterprise: true },
  { name: "Video Call Support", growth: false, enterprise: true },
  { name: "Dedicated Account Manager", growth: false, enterprise: true },
  { name: "Advanced Analytics", growth: false, enterprise: true },
  { name: "Custom Training Programs", growth: false, enterprise: true },
  { name: "Custom SLA Agreements", growth: false, enterprise: true },
  { name: "Priority Onboarding", growth: false, enterprise: true },
  { name: "API Integrations", growth: false, enterprise: true },
];

export default function Pricing() {
  const { viewMode } = useViewMode();
  const isClient = viewMode === 'client';
  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/5 to-transparent" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-1.5 bg-pink-500/10 border border-pink-500/30 text-pink-400 text-sm font-mono rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Transparent Pricing
          </motion.span>
          <h2 className="text-foreground mb-6">
            Simple, Transparent{" "}
            <span className="text-pink-400">Pricing</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Choose the plan that fits your needs. Scale up anytime, no hidden
            fees or surprises.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          {packages.map((pkg, index) => {
            const isMagenta = pkg.color === "magenta";
            return (
              <motion.div
                key={pkg.name}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
                data-testid={`pricing-${pkg.name.toLowerCase()}`}
              >
                <div
                  className={`relative p-8 border-2 bg-card/50 backdrop-blur-sm transition-all duration-300 h-full ${
                    isMagenta
                      ? "border-pink-500 hover:shadow-lg hover:shadow-pink-500/20"
                      : "border-cyan-400/50 hover:border-cyan-400 hover:shadow-lg hover:shadow-cyan-400/20"
                  }`}
                >
                  {/* Corner Accents */}
                  <div
                    className={`absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 ${
                      isMagenta ? "border-pink-500" : "border-cyan-400"
                    }`}
                  />
                  <div
                    className={`absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 ${
                      isMagenta ? "border-pink-500" : "border-cyan-400"
                    }`}
                  />

                  {/* Popular Badge */}
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-4 py-1 bg-pink-500 text-white font-mono font-bold text-xs uppercase tracking-wider">
                        <Sparkles className="w-3 h-3" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Package Header */}
                  <div className={`mb-6 ${pkg.popular ? "pt-4" : ""}`}>
                    <h3 className="text-foreground font-mono font-bold text-2xl mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">{pkg.tagline}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-8">
                    {isClient ? (
                      <>
                        <div
                          className={`text-4xl font-mono font-bold ${
                            isMagenta ? "text-pink-400" : "text-cyan-400"
                          }`}
                        >
                          {pkg.price}
                          <span className="text-lg text-muted-foreground">/hr</span>
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">
                          {pkg.priceNote}
                        </p>
                        <p className="text-muted-foreground/60 text-xs mt-1">
                          {pkg.minimumNote}
                        </p>
                      </>
                    ) : (
                      <>
                        <div
                          className={`text-4xl font-mono font-bold ${
                            isMagenta ? "text-pink-400" : "text-cyan-400"
                          }`}
                        >
                          Contact Sales
                        </div>
                        <p className="text-muted-foreground text-sm mt-1">
                          Custom pricing available
                        </p>
                      </>
                    )}
                  </div>

                  {/* CTA Button */}
                  <motion.a
                    href={CALENDLY_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full mb-8 group/btn ${
                      isMagenta ? "neon-button-magenta" : "neon-button"
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {pkg.cta}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </motion.a>

                  {/* Features List */}
                  <div
                    className={`space-y-3 border-t pt-6 ${
                      isMagenta ? "border-pink-500/20" : "border-cyan-400/20"
                    }`}
                  >
                    {pkg.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check
                            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                              isMagenta ? "text-pink-400" : "text-cyan-400"
                            }`}
                          />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                        )}
                        <span
                          className={`text-sm ${
                            feature.included
                              ? feature.highlight
                                ? isMagenta
                                  ? "text-pink-400 font-semibold"
                                  : "text-cyan-400 font-semibold"
                                : "text-muted-foreground/80"
                              : "text-muted-foreground/40"
                          }`}
                        >
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {isClient && (
            <h3 className="text-foreground font-mono font-bold text-2xl mb-8 text-center">
              Feature Comparison
            </h3>
          )}
          {isClient && (
            <div className="overflow-x-auto">
              <table className="w-full border border-border">
              <thead>
                <tr className="border-b border-border bg-card/30">
                  <th className="px-6 py-4 text-left text-foreground font-mono font-bold">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-center text-cyan-400 font-mono font-bold">
                    Growth
                  </th>
                  <th className="px-6 py-4 text-center text-pink-400 font-mono font-bold">
                    Enterprise
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, index) => (
                  <tr
                    key={index}
                    className="border-b border-border hover:bg-card/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-muted-foreground/80 text-sm">
                      {feature.name}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof feature.growth === "boolean" ? (
                        feature.growth ? (
                          <Check className="w-5 h-5 text-cyan-400 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                        )
                      ) : isClient ? (
                        <span className="text-cyan-400 font-mono text-sm">
                          {feature.growth}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {typeof feature.enterprise === "boolean" ? (
                        feature.enterprise ? (
                          <Check className="w-5 h-5 text-pink-400 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                        )
                      ) : isClient ? (
                        <span className="text-pink-400 font-mono text-sm">
                          {feature.enterprise}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
